import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface GeckoShipDeckBittJSON {
  type: GeckoFeatureModelType.ShipDeckBitt
  name: string
  feature_geometry: {
    x: number
    y: number
    nominal_fitting_size: BittNominalFittingSize
  }
}

export enum BittNominalFittingSize {
  US4 = 'US4',
  US6 = 'US6',
  US8 = 'US8',
  US10 = 'US10',
  US12 = 'US12',
}

export const NewGeckoShipDeckBittJSON: GeckoShipDeckBittJSON = {
  type: GeckoFeatureModelType.ShipDeckBitt,
  name: 'New Ship Deck Bitt',
  feature_geometry: {
    x: 0,
    y: 0,
    nominal_fitting_size: BittNominalFittingSize.US6,
  },
}

export class GeckoShipDeckBitt extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckBitt
  readonly nominalFittingSize: BittNominalFittingSize
  readonly geometry: GeckoComponentCoords

  private constructor(
    id: string,
    name: string,
    nominalFittingSize: BittNominalFittingSize,
    geometry: GeckoComponentCoords
  ) {
    super(id)
    this.name = name
    this.nominalFittingSize = nominalFittingSize
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    bittID: string,
    bittJSON: GeckoShipDeckBittJSON
  ): GeckoShipDeckBitt {
    const featureGeometryData = bittJSON.feature_geometry as Record<
      string,
      number | string
    >
    const bittGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x as number,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y as number,
      GeckoMeasurementUnit.Meters
    )

    const bittName = bittJSON.name as string

    const nominalFittingSize =
      featureGeometryData.nominal_fitting_size as BittNominalFittingSize

    return new GeckoShipDeckBitt(
      bittID,
      bittName,
      nominalFittingSize,
      bittGeometry
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckBittJSON>
  ): Map<string, GeckoShipDeckBitt> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckBitt.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckBitt>())
  }

  asJSON() {
    return generateBitt(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.nominalFittingSize
    )
  }
}

export function generateBitt(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  nominalFittingSize: BittNominalFittingSize
): GeckoShipDeckBittJSON {
  return {
    type: GeckoFeatureModelType.ShipDeckBitt,
    name: name,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
      nominal_fitting_size: nominalFittingSize,
    },
  }
}

export const ShipDeckBittFormFields = (bittJSON: GeckoShipDeckBittJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Bitt Name',
      value: bittJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: bittJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: bittJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'nominal_fitting_size',
      label: 'Nominal Fitting Size',
      value: bittJSON.feature_geometry.nominal_fitting_size,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: Object.keys(BittNominalFittingSize).map((key) => ({
        key: BittNominalFittingSize[key as keyof typeof BittNominalFittingSize],
        value: key,
        label: key.replace('US', 'US '),
      })),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const ShipDeckBittJSONFromForm = (formFields: FormField[]) => {
  return generateBitt(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'nominal_fitting_size')
      ?.value as BittNominalFittingSize
  )
}

export const ShipDeckBittFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckBitt,
  title: 'Bitts',
  singularName: 'Bitt',
  fields: ['x', 'y', 'nominal_fitting_size'],
}
